import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BrandIcon from '../../../shared/components/brand-icon/BrandIcon';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import { formatCount } from '../../../shared/utils/utils';

export interface BotsActivityBreakdownListRow {
  label: string;
  /** Optional muted second line shown below `label` (e.g. raw path under "Homepage"). */
  secondaryLabel?: string;
  /** Optional native title attribute — useful when `label` is truncated. */
  title?: string;
  count: number;
  /** Signed delta vs previous period, used by row right-side meta (e.g. Top URLs). */
  changePct?: number | null;
  /** Most recent visit timestamp for the row, used by row right-side meta. */
  lastSeenAt?: string | null;
  iconClass?: string | null;
  color?: string;
}

interface BotsActivityBreakdownListProps {
  rows: BotsActivityBreakdownListRow[];
  title: string;
  titleIconClass: string;
  /** i18n key shown as a tooltip next to the header title. */
  tooltipKey?: string;
  iconSize?: 'sm' | 'md';
  emptyMessageKey?: string;
  /** Falls back to `max(row.count)` when omitted — useful when each card has its own scale. */
  maxCount?: number;
  /** When true, render Change / Last indexed columns on the row right side. */
  showMetaColumns?: boolean;
  /**
   * Collapse the list to its first N rows and expose a "Show more / Show less"
   * toggle. Omit (or pass 0) to always render every row.
   */
  initialLimit?: number;
}

const formatRelativeTime = (
  iso: string | null | undefined,
  t: (k: string, opts?: Record<string, unknown>) => string
): string => {
  if (!iso) return t('botsActivity.never');
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return t('botsActivity.never');
  const diffMs = Date.now() - ts;
  if (diffMs < 60_000) return t('botsActivity.timeAgo.justNow');
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return t('botsActivity.timeAgo.minutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('botsActivity.timeAgo.hours', { count: hours });
  const days = Math.floor(hours / 24);
  return t('botsActivity.timeAgo.days', { count: days });
};

const BotsActivityBreakdownList: React.FC<BotsActivityBreakdownListProps> = ({
  rows,
  title,
  titleIconClass,
  tooltipKey,
  iconSize = 'sm',
  emptyMessageKey = 'noDataYet',
  maxCount,
  showMetaColumns = false,
  initialLimit = 0,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const computedMax = maxCount ?? rows.reduce((m, r) => Math.max(m, r.count), 0);
  const safeMax = computedMax > 0 ? computedMax : 1;

  // Scale bar widths against the FULL row set so the collapsed view shows the
  // same fill widths the user would see when expanded — keeps the leaderboard
  // visually stable across toggles.
  const canCollapse = initialLimit > 0 && rows.length > initialLimit;
  const visibleRows = canCollapse && !expanded ? rows.slice(0, initialLimit) : rows;
  const hiddenCount = rows.length - initialLimit;

  return (
    <div className="breakdown-list">
      <div className="breakdown-list__header">
        <span className={`${titleIconClass} ico--sm breakdown-list__header-icon`} aria-hidden="true" />
        <span className="breakdown-list__header-title">{title}</span>
        {tooltipKey && <CustomTooltip tooltipText={tooltipKey} addMargin={false} />}
      </div>

      <div className="breakdown-list__body">
        {rows.length === 0 ? (
          <div className="breakdown-list__empty">{t(emptyMessageKey)}</div>
        ) : (
          <ul className="breakdown-list__rows">
            {showMetaColumns && (
              <li className="breakdown-list__row breakdown-list__row--head" aria-hidden="true">
                <div className="breakdown-list__row-head breakdown-list__row-head--columns">
                  <div className="breakdown-list__row-label breakdown-list__col-heading" />
                  <span className="breakdown-list__row-count breakdown-list__col-heading">
                    {t('botsActivity.visits')}
                  </span>
                  <span className="breakdown-list__row-change breakdown-list__col-heading">
                    {t('botsActivity.change')}
                  </span>
                  <span className="breakdown-list__row-last-indexed breakdown-list__col-heading">
                    {t('botsActivity.lastIndexed')}
                  </span>
                </div>
              </li>
            )}
            {visibleRows.map((row, idx) => {
              const sharePct = Math.max(2, Math.round((row.count / safeMax) * 100));
              const change = row.changePct;
              const changeClass =
                change == null
                  ? ''
                  : change > 0
                    ? 'breakdown-list__row-change--up'
                    : change < 0
                      ? 'breakdown-list__row-change--down'
                      : 'breakdown-list__row-change--flat';
              return (
                <li className="breakdown-list__row" key={`${row.label}-${idx}`}>
                  <div className="breakdown-list__row-head">
                    <div className="breakdown-list__row-label" title={row.title ?? row.label}>
                      <BrandIcon
                        iconClass={row.iconClass}
                        color={row.color}
                        name={row.label}
                        size={iconSize}
                      />
                      <span className="breakdown-list__row-name-stack">
                        <span className="breakdown-list__row-name">{row.label}</span>
                        {row.secondaryLabel && (
                          <span className="breakdown-list__row-secondary">{row.secondaryLabel}</span>
                        )}
                      </span>
                    </div>
                    <span className="breakdown-list__row-count">{formatCount(row.count)}</span>
                    {showMetaColumns && (
                      <>
                        <span className={`breakdown-list__row-change ${changeClass}`.trim()}>
                          {change == null ? '—' : `${change > 0 ? '↑' : change < 0 ? '↓' : ''} ${Math.abs(change)}`}
                        </span>
                        <span className="breakdown-list__row-last-indexed">
                          {formatRelativeTime(row.lastSeenAt, t)}
                        </span>
                      </>
                    )}
                  </div>
                  <div
                    className="breakdown-list__bar"
                    role="progressbar"
                    aria-label={`${row.label}: ${row.count}`}
                    aria-valuenow={sharePct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <span
                      className="breakdown-list__bar-fill"
                      style={{
                        width: `${sharePct}%`,
                        backgroundColor: row.color ?? undefined,
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {canCollapse && (
          <button
            type="button"
            className="show-more-toggle"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
          >
            <span className={`ico--bottom ico--xs ${expanded ? 'ico--rotate' : ''}`} aria-hidden="true" />
            {expanded
              ? t('botsActivity.showLess')
              : t('botsActivity.showMore', { count: hiddenCount })}
          </button>
        )}
      </div>
    </div>
  );
};

export default BotsActivityBreakdownList;
